import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Spinner } from '@blueprintjs/core';
import { FormTemplate } from 'data/template/template.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import RadiusParserOptionsForm from 'routes/RadiusParserOptions/components/RadiusParserOptionsForm';
import { Ref } from 'routes/RadiusParserOptions/components/RadiusParserOptionsForm/RadiusParserOptionsForm';
import { RootState } from 'data/root.reducers';
import * as radiusParserOptionsSelectors from 'data/radiusParserOptions/radiusParserOptions.selectors';
import * as radiusParserOptionsActions from 'data/radiusParserOptions/radiusParserOptions.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { RadiusParserOptions } from 'data/radiusParserOptions/radiusParserOptions.types';
import ProgressDialog from 'components/ProgressDialog';
import { radiusParserOptionsTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateFieldChanges
} from '../../utils/radiusParserOptionsTemplate.adapter';
import {
  getAuditDetailsToSubmit
} from '../../utils/radiusParserOptionsDataToSubmit';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  radiusParserOptions: radiusParserOptionsSelectors.getRadiusParserOptions(state),
  radiusParserOptionsAdaptedTemplate: adaptTemplateVisibilityMode(radiusParserOptionsTemplate, 'edit'),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  isRadiusParserOptionsLoading: radiusParserOptionsSelectors.isRadiusParserOptionsLoading(state),
  isRadiusParserOptionsSaving: radiusParserOptionsSelectors.isRadiusParserOptionsSaving(state),
});

const mapDispatch = {
  goToRadiusParserOptionsView: radiusParserOptionsActions.goToRadiusParserOptionsView,
  getRadiusParserOptions: radiusParserOptionsActions.getRadiusParserOptions,
  updateRadiusParserOptions: radiusParserOptionsActions.updateRadiusParserOptions,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const RadiusParserOptionsEdit = ({
  history,
  radiusParserOptions,
  radiusParserOptionsAdaptedTemplate,
  isAuditEnabled,
  userName,
  userId,
  isRadiusParserOptionsLoading,
  isRadiusParserOptionsSaving,
  getRadiusParserOptions,
  goToRadiusParserOptionsView,
  updateRadiusParserOptions,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<RadiusParserOptions>>({});
  const [template, setTemplate] = useState<FormTemplate>(radiusParserOptionsAdaptedTemplate as FormTemplate);
  const { t } = useTranslation();

  useEffect(() => {
    getRadiusParserOptions();
  }, [getRadiusParserOptions]);

  useEffect(() => {
    if (radiusParserOptionsAdaptedTemplate == null || isRadiusParserOptionsSaving) {
      return;
    }

    if (radiusParserOptions != null) {
      setFormData(radiusParserOptions);
    }

    const clonedTemplate = cloneDeep(radiusParserOptionsAdaptedTemplate) as FormTemplate;
    clonedTemplate.formId = Date.now().toString();

    setTemplate(clonedTemplate as FormTemplate);

  }, [radiusParserOptions, radiusParserOptionsAdaptedTemplate, isRadiusParserOptionsSaving]);

  const handleCancel = () => goToRadiusParserOptionsView(history);

  const handleClickSave = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const radiusParserOptionsData = formRef.current.getValues();

    const payload = {
      data: radiusParserOptionsData,
      auditDetails: getAuditDetailsToSubmit(
        radiusParserOptionsData,
        radiusParserOptions,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    updateRadiusParserOptions((radiusParserOptions?.id) as string, payload, history);
  };

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {

    setTemplate(adaptTemplateFieldChanges(template, fieldName, value));

    const newFormData = cloneDeep(formData as RadiusParserOptions);

    setFormData({
      ...newFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (template) {
      return (
        <RadiusParserOptionsForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };

  const renderFormPanel = () => {
    return (
      <FormPanel title={t('radiusParserOptions.edit.title')}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (isRadiusParserOptionsLoading) {
    return <Spinner aria-label="Loading..." />;
  }

  if (!radiusParserOptions?.id) {
    return <div />;
  }

  return (
    <div data-testid="RadiusParserOptionsEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
      <ProgressDialog
        open={isRadiusParserOptionsSaving}
        title={t('radiusParserOptions.edit.editing')}
      />
    </div>
  );
};

export default withRouter(connector(RadiusParserOptionsEdit));
